﻿// Homepage-only portraits: one shared body, canonical masks rendered at portrait density.
import { PALETTE as P } from './theme';
import { CREW } from './crew';
import { dk, mix } from './hd-kit';
import { poly } from './runner-detail';

/** Trim the original SVG and quantize it to crisp theme ramps, preserving its actual mask geometry. */
function portraitMask(image: HTMLImageElement, color: string): HTMLCanvasElement {
  const source=document.createElement('canvas'); source.width=source.height=512;
  const s=source.getContext('2d')!; s.drawImage(image,0,0,512,512);
  const pixels=s.getImageData(0,0,512,512).data;
  let left=512,top=512,right=0,bottom=0;
  for(let y=0;y<512;y++) for(let x=0;x<512;x++) {
    if(pixels[(y*512+x)*4+3]<96) continue;
    left=Math.min(left,x); right=Math.max(right,x); top=Math.min(top,y); bottom=Math.max(bottom,y);
  }
  const result=document.createElement('canvas'); result.width=56; result.height=64;
  if(left>right) return result;
  const g=result.getContext('2d')!,w=right-left+1,h=bottom-top+1;
  const scale=Math.min(54/w,62/h),dw=Math.round(w*scale),dh=Math.round(h*scale);
  g.drawImage(source,left,top,w,h,Math.floor((56-dw)/2),64-dh,dw,dh);
  const rgb=(c:string)=>[parseInt(c.slice(1,3),16),parseInt(c.slice(3,5),16),parseInt(c.slice(5,7),16)];
  const ramp=[dk(P.midnight,.3),P.midnight,mix(P.midnight,P.slate,.6),P.slate,mix(P.slate,P.offWhite,.2),color,mix(color,P.offWhite,.35),P.offWhite].map(rgb);
  const data=g.getImageData(0,0,56,64);
  for(let i=0;i<data.data.length;i+=4) {
    if(data.data[i+3]<96) { data.data[i+3]=0; continue; }
    let best=ramp[0],distance=Infinity;
    for(const c of ramp) {
      const d=(data.data[i]-c[0])**2+(data.data[i+1]-c[1])**2+(data.data[i+2]-c[2])**2;
      if(d<distance) { distance=d; best=c; }
    }
    data.data.set([...best,255],i);
  }
  // A single hard shell bevel gives the face volume without soft glow or changing LED markings.
  const original=new Uint8ClampedArray(data.data);
  for(let y=0;y<64;y++) for(let x=0;x<56;x++) {
    const i=(y*56+x)*4;
    if(original[i+3]===0) continue;
    const channels=[original[i],original[i+1],original[i+2]];
    if(Math.max(...channels)-Math.min(...channels)>60 || channels.reduce((a,b)=>a+b,0)>350) continue;
    const open=(xx:number,yy:number)=>xx<0||xx>=56||yy<0||yy>=64||original[(yy*56+xx)*4+3]===0;
    const edge=open(x-1,y)||open(x,y-1) ? ramp[4] : open(x+1,y)||open(x,y+1) ? ramp[1] : null;
    if(edge) data.data.set([...edge,255],i);
  }
  g.putImageData(data,0,0); return result;
}

export function paintTitleCrew(canvas: HTMLCanvasElement, slot: number): () => void {
  const crew=CREW[slot],art=document.createElement('canvas'); art.width=70; art.height=112;
  canvas.width=140; canvas.height=224;
  const g=art.getContext('2d')!,out=canvas.getContext('2d')!;
  g.imageSmoothingEnabled=out.imageSmoothingEnabled=false;
  let stopped=false,mask:HTMLCanvasElement|undefined;
  const image=new Image(),ink=dk(P.midnight,.35),deep=mix(P.midnight,P.slate,.3);
  const cloth=mix(P.midnight,P.slate,.7),light=mix(P.slate,P.offWhite,.23),trim=mix(cloth,crew.color,.48);
  const r=(x:number,y:number,w:number,h:number,c:string)=>{g.fillStyle=c;g.fillRect(x,y,w,h);};
  const shape=(points:number[][],color:string)=>poly(g,points,color);
  const paint=()=>{
    if(stopped) return;
    g.clearRect(0,0,70,112);g.save();g.translate(35,0);
    r(-23,107,46,1,crew.color+'28');r(-17,106,34,1,crew.color+'18');
    // Identical height, shoulders, waist, stance and equipment for every runner.
    shape([[-13,68],[13,68],[14,83],[15,99],[18,101],[18,105],[3,105],[1,83],[-1,83],[-4,105],[-19,105],[-19,101],[-15,99],[-14,83]],ink);
    shape([[-11,70],[-2,70],[-3,83],[-6,99],[-13,99],[-11,82]],cloth);
    shape([[2,70],[11,70],[11,83],[13,99],[6,99],[3,83]],deep);
    r(-12,82,8,5,deep);r(4,82,7,5,cloth);r(-11,82,5,1,light);r(5,82,4,1,light);
    r(-14,96,8,4,ink);r(5,96,8,4,ink);r(-17,102,11,1,light);r(5,102,11,1,light);
    r(-17,104,12,1,deep);r(4,104,13,1,deep);
    // Common short technical jacket, collar, zipper and pockets; no bulky or coat variants.
    shape([[-8,31],[-15,35],[-17,45],[-14,54],[-14,69],[14,69],[14,54],[17,45],[15,35],[8,31]],ink);
    shape([[-8,33],[-13,37],[-12,51],[-11,65],[-2,65],[-2,39]],cloth);
    shape([[2,39],[8,33],[13,37],[12,51],[11,65],[2,65]],deep);
    shape([[-8,32],[-1,38],[-5,44],[-11,35]],light);shape([[8,32],[1,38],[5,44],[11,35]],cloth);
    r(-1,40,2,23,ink);r(0,41,1,20,light);r(-11,48,7,8,deep);r(-10,48,6,1,light);
    r(4,48,7,8,cloth);r(5,48,5,1,light);r(-11,58,6,1,trim);r(5,58,6,1,trim);
    r(-12,64,24,4,ink);r(-3,64,6,3,light);r(-2,65,4,1,deep);
    // Same relaxed sleeves and gloves.
    shape([[-15,36],[-20,39],[-21,52],[-18,68],[-12,68],[-14,53],[-12,42]],ink);
    shape([[-16,39],[-18,41],[-18,52],[-15,55],[-14,43]],cloth);
    r(-18,42,1,10,light);r(-18,54,5,4,deep);r(-17,58,4,8,cloth);r(-17,65,5,6,ink);r(-16,66,2,1,light);
    shape([[15,36],[20,39],[21,52],[18,68],[12,68],[14,53],[12,42]],ink);
    shape([[16,39],[18,41],[18,52],[15,55],[14,43]],deep);
    r(18,43,1,9,light);r(13,54,5,4,cloth);r(13,58,4,8,deep);r(12,65,5,6,ink);r(14,66,2,1,light);
    r(-18,59,4,1,trim);r(14,59,4,1,trim);
    shape([[-10,12],[-7,8],[7,8],[10,12],[11,29],[7,35],[-7,35],[-11,29]],ink);
    r(-10,16,1,12,light);r(9,17,1,11,cloth);
    g.restore();out.clearRect(0,0,140,224);out.drawImage(art,0,0,140,224);
    if(mask) out.drawImage(mask,42,6);
  };
  image.onload=()=>{if(!stopped){mask=portraitMask(image,crew.color);paint();}};
  image.src='data:image/svg+xml;charset=utf-8,'+encodeURIComponent(crew.svg);
  paint();
  return()=>{stopped=true;image.onload=null;};
}
